import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "@/lib/api";
import { Sparkles, ArrowLeft, Mail, Key, CheckCircle } from "lucide-react";
import "./Login.css";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "reset" | "done">("email");
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async () => {
    if (!email) { setError("Please enter your email"); return; }
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest<{ success: boolean; resetToken?: string }>("/stores/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      if (result.resetToken) {
        setResetToken(result.resetToken);
        setTokenInput(result.resetToken);
      }
    } catch {
      // Intentionally show the same page whether email exists or not
    }
    setStep("reset");
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!tokenInput) { setError("Please enter the reset token"); return; }
    if (!newPassword || newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords don't match"); return; }
    setLoading(true);
    setError("");
    try {
      await apiRequest("/stores/reset-password", {
        method: "POST",
        body: JSON.stringify({ token: tokenInput, newPassword }),
      });
      setStep("done");
    } catch (err: any) {
      setError(err.message || "Failed to reset password");
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Sparkles size={32} />
          </div>
          <h1>Showroom AiPro</h1>
          <p>Reset Your Password</p>
        </div>

        {step === "email" && (
          <div className="login-form">
            <div className="form-group">
              <label><Mail size={14} /> Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                placeholder="Enter your registered email"
                onKeyDown={(e) => e.key === "Enter" && handleRequestReset()}
              />
            </div>
            <button className="btn btn-primary login-submit" onClick={handleRequestReset} disabled={loading}>
              {loading ? "Sending..." : "Request Password Reset"}
            </button>
          </div>
        )}

        {step === "reset" && (
          <div className="login-form">
            {resetToken && (
              <div style={{ background: "#ECFDF5", border: "1px solid #10B981", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                <strong>Reset token generated.</strong> In production this would be emailed. For now, it's been auto-filled below.
              </div>
            )}
            <div className="form-group">
              <label><Key size={14} /> Reset Token</label>
              <input
                type="text"
                value={tokenInput}
                onChange={(e) => { setTokenInput(e.target.value); setError(""); }}
                placeholder="Enter your reset token"
              />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                placeholder="At least 6 characters"
              />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                placeholder="Confirm your new password"
                onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
              />
            </div>
            <button className="btn btn-primary login-submit" onClick={handleResetPassword} disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        )}

        {step === "done" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <CheckCircle size={48} color="#10B981" style={{ marginBottom: 12 }} />
            <h3 style={{ marginBottom: 8 }}>Password Reset Successfully</h3>
            <p style={{ color: "#64748B", marginBottom: 20 }}>You can now log in with your new password.</p>
            <button className="btn btn-primary" onClick={() => navigate("/login")}>
              Go to Login
            </button>
          </div>
        )}

        {error && <p className="login-error">{error}</p>}

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            onClick={() => navigate("/login")}
            style={{ background: "none", border: "none", color: "#64748B", cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <ArrowLeft size={14} /> Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
